import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { COUNTRIES } from "@/lib/countries";

const Select = ({ light, className = "", cls, children, ...p }) => (
  <div className={`relative ${className}`}>
    <select {...p} className={`${cls} h-11 appearance-none pr-9`}>{children}</select>
    <ChevronDown className={`w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${light ? "text-slate-400" : "text-slate-500"}`} />
  </div>
);

const PhoneInput = ({ f, value, onChange, input, light, accent }) => {
  const [iso, setIso] = useState(() => {
    const dial = (value || "").split(" ")[0];
    return COUNTRIES.find((c) => c[2] === dial)?.[1] || "US";
  });
  const dial = COUNTRIES.find((c) => c[1] === iso)?.[2] || "+1";
  const num = (value || "").split(" ").slice(1).join(" ");
  const emit = (d, n) => onChange(n.trim() ? `${d} ${n.trim()}` : "");
  return (
    <div className="flex gap-2" data-testid={`lead-field-${f.id}`}>
      <Select light={light} className="w-32 shrink-0" cls={input} data-testid={`lead-field-${f.id}-code`} value={iso}
        onChange={(e) => { setIso(e.target.value); emit(COUNTRIES.find((c) => c[1] === e.target.value)[2], num); }}>
        {COUNTRIES.map(([n, i, d]) => <option key={i} value={i}>{i} {d}</option>)}
      </Select>
      <input data-testid={`lead-field-${f.id}-number`} type="tel" inputMode="tel" value={num} placeholder={f.placeholder || "Phone number"}
        onChange={(e) => emit(dial, e.target.value.replace(/[^\d\s()-]/g, ""))} className={`${input} h-11 flex-1 min-w-0`} style={{ caretColor: accent }} />
    </div>
  );
};

export const FieldControl = ({ field: f, value, onChange, light, accent }) => {
  const txt = light ? "text-slate-900 placeholder:text-slate-400 bg-white border-slate-300" : "text-white placeholder:text-slate-500 bg-white/5 border-white/10";
  const input = `w-full px-4 rounded-md text-sm focus:outline-none transition-colors border ${txt}`;
  if (f.type === "text")
    return <input data-testid={`lead-field-${f.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder} className={`${input} h-11`} style={{ caretColor: accent }} />;
  if (f.type === "phone") return <PhoneInput f={f} value={value} onChange={onChange} input={input} light={light} accent={accent} />;
  if (f.type === "dropdown" || f.type === "country") {
    const opts = f.type === "country" ? COUNTRIES.map((c) => c[0]) : f.options;
    return (
      <Select light={light} cls={input} data-testid={`lead-field-${f.id}`} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>{f.placeholder || (f.type === "country" ? "Select your country" : "Select…")}</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </Select>
    );
  }
  const multi = f.type === "checkboxes";
  return (
    <div className="space-y-2" data-testid={`lead-field-${f.id}`}>
      {f.options.map((o) => {
        const on = multi ? (value || []).includes(o) : value === o;
        return (
          <label key={o} className={`flex items-center gap-2.5 text-sm cursor-pointer ${light ? "text-slate-700" : "text-slate-300"}`}>
            <input type={multi ? "checkbox" : "radio"} checked={on} style={{ accentColor: accent }}
              onChange={() => onChange(multi ? (on ? (value || []).filter((x) => x !== o) : [...(value || []), o]) : o)} />
            {o}
          </label>
        );
      })}
    </div>
  );
};

const Lnk = ({ href, children, testid }) => (
  <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className="underline underline-offset-2 hover:opacity-80">{children}</a>
);

const partyLinks = (p, i) => [
  p.privacy_url && <Lnk key="p" href={p.privacy_url} testid={`legal-privacy-link-${i}`}>Privacy Policy</Lnk>,
  p.terms_url && <Lnk key="t" href={p.terms_url} testid={`legal-terms-link-${i}`}>Terms &amp; Conditions</Lnk>,
].filter(Boolean).reduce((acc, el, j) => (j ? [...acc, " · ", el] : [el]), []);

export const PartyLinks = ({ parties, idx, prefix, sep = " — " }) => {
  const list = idx.filter((i) => parties[i]);
  if (!list.length) return null;
  return (
    <span data-testid={`${prefix}-legal-links`}>{sep}{list.map((i, k) => (
      <span key={i} data-testid={`${prefix}-legal-party-${i}`}>
        {k > 0 && (k === list.length - 1 ? " and " : ", ")}
        <span className="font-semibold">{parties[i].name}</span> ({partyLinks(parties[i], `${prefix}-${i}`)})
      </span>
    ))}</span>
  );
};

export const LegalAgree = ({ legal, checked, onChange, dim, accent }) => {
  if (!legal?.require_agree || !legal.parties?.length) return null;
  return (
    <label data-testid="legal-agree" className={`flex items-start gap-2.5 text-xs leading-relaxed cursor-pointer ${dim}`}>
      <input type="checkbox" data-testid="legal-agree-checkbox" className="mt-0.5" checked={checked} style={{ accentColor: accent }} onChange={(e) => onChange(e.target.checked)} />
      <span>I agree to the Privacy Policy and Terms &amp; Conditions of
        <PartyLinks parties={legal.parties} idx={legal.parties.map((_, i) => i)} prefix="agree" sep=" " /> *</span>
    </label>
  );
};
